import { isPlatformBrowser } from '@angular/common';
import {
	Component,
	DestroyRef,
	ElementRef,
	EventEmitter,
	HostListener,
	Inject,
	inject,
	OnDestroy,
	OnInit,
	Output,
	PLATFORM_ID,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { AuthService } from '@services/auth/auth.service';
import { CurrentUserService } from '@services/auth/current-user.service';
import { BrandingService } from '@services/branding/branding.service';
import { ViewportService } from '@services/geo/viewport.service';
import { MenuService } from '@services/shared/menu.service';
import { SharedModule } from '@shared/modules/shared.module';
import { BRANDING } from 'app/core/constants/branding.constants';
import { filter, Subscription } from 'rxjs';
import { SidebarMenuItem, SidebarState } from './sidebar.model';

@Component({
	selector: 'app-sidebar',
	imports: [SharedModule, RouterLink, RouterLinkActive],
	templateUrl: './sidebar.component.html',
	styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnInit, OnDestroy {
	private readonly destroyRef = inject(DestroyRef);

	@Output() sidebarToggle = new EventEmitter<boolean>();

	sidebarState: SidebarState = 'expanded';
	mobileSidebarVisible = false;
	expandedSubmenuId: string | null = null;
	isProfileMenuOpen = false;

	currentUser: any = null;
	userName = 'Usuario';
	userRole = '';
	currentUserId?: number;
	isClient = false;
	brandLogo: string | null = null;
	readonly wordmark = BRANDING.sidebarLogo;
	// Mobile menu header: same logo + wordmark as the mobile topbar
	readonly mobileLogo = BRANDING.logo;
	readonly mobileWordmark = BRANDING.topbarWordmark;
	readonly userSticker = 'assets/layout/images/sticker-edu.png';
	brandName = '';

	menuItems: SidebarMenuItem[] = [];

	private subscriptions = new Subscription();

	constructor(
		private el: ElementRef,
		private authService: AuthService,
		private viewportService: ViewportService,
		private menuService: MenuService,
		private currentUserService: CurrentUserService,
		private brandingService: BrandingService,
		private router: Router,
		@Inject(PLATFORM_ID) private platformId: object,
	) {}

	/** Single list: config items go last */
	get mainItems(): SidebarMenuItem[] {
		return [
			...this.menuItems.filter((i) => i.section !== 'config'),
			...this.menuItems.filter((i) => i.section === 'config'),
		];
	}

	get isExpanded(): boolean {
		return this.sidebarState === 'expanded';
	}

	get isCollapsed(): boolean {
		return this.sidebarState === 'collapsed';
	}

	get isMobile(): boolean {
		return this.sidebarState === 'mobile';
	}

	@HostListener('window:resize')
	onResize(): void {
		if (!isPlatformBrowser(this.platformId)) return;
		this.updateSidebarState();
	}

	/** Escape closes the open menus: floating submenu, profile dropdown and the mobile drawer. */
	@HostListener('document:keydown.escape')
	onEscape(): void {
		if (this.isCollapsed && this.expandedSubmenuId) {
			this.expandedSubmenuId = null;
		} else if (this.isProfileMenuOpen) {
			this.isProfileMenuOpen = false;
		} else if (this.isMobile && this.mobileSidebarVisible) {
			this.closeMobileSidebar();
		}
	}

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: Event): void {
		const target = event.target as HTMLElement;

		// Close floating submenu if clicking outside (collapsed mode)
		if (this.isCollapsed && this.expandedSubmenuId) {
			const floatingSubmenu = this.el.nativeElement.querySelector('.sidebar__floating-submenu');
			const parentItem = floatingSubmenu?.closest('.sidebar__item');
			if (parentItem && !parentItem.contains(target)) {
				this.expandedSubmenuId = null;
			}
		}
	}

	ngOnInit(): void {
		this.initUser();
		// Saving the own profile updates the stored user: show the new name without logging in again
		this.currentUserService.currentUser$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.initUser());
		this.buildMenu();
		this.updateSidebarState();

		// Branding logo/name
		this.subscriptions.add(
			this.brandingService.branding$.subscribe((branding) => {
				this.brandLogo = branding?.appIcon ?? branding?.logo ?? null;
				this.brandName = branding?.name ?? '';
			}),
		);

		// Sync mobile sidebar with MenuService
		this.subscriptions.add(
			this.menuService.menuState$.subscribe((state) => {
				if (this.viewportService.isMobileView()) {
					this.mobileSidebarVisible = state['sidebar'] || false;
				}
			}),
		);

		// Close mobile sidebar on navigation
		this.subscriptions.add(
			this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
				if (this.viewportService.isMobileView()) {
					this.closeMobileSidebar();
				}
			}),
		);
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}

	// --- State management ---

	private updateSidebarState(): void {
		const width = window.innerWidth;
		if (width < 844) {
			// Mobile: sidebar hidden by default (opened via hamburger)
			if (this.sidebarState !== 'mobile') {
				this.sidebarState = 'mobile';
			}
			this.mobileSidebarVisible = false;
			this.menuService.closeMenu('sidebar');
			this.sidebarToggle.emit(false);
		} else if (width < 1200) {
			this.sidebarState = 'collapsed';
			this.expandedSubmenuId = null;
			this.sidebarToggle.emit(true);
		} else {
			this.sidebarState = 'expanded';
			this.sidebarToggle.emit(true);
		}
	}

	toggleSidebar(): void {
		if (this.sidebarState === 'expanded') {
			this.sidebarState = 'collapsed';
			this.expandedSubmenuId = null;
		} else if (this.sidebarState === 'collapsed') {
			this.sidebarState = 'expanded';
		}
		this.sidebarToggle.emit(this.isExpanded);
	}

	closeMobileSidebar(): void {
		this.mobileSidebarVisible = false;
		this.isProfileMenuOpen = false;
		this.menuService.closeMenu('sidebar');
		this.sidebarToggle.emit(false);
	}

	// --- Menu interactions ---

	toggleSubmenu(item: SidebarMenuItem): void {
		if (!item.children?.length) return;
		this.expandedSubmenuId = this.expandedSubmenuId === item.id ? null : item.id;
	}

	handleItemClick(item: SidebarMenuItem): void {
		if (item.action) {
			item.action();
			return;
		}
		if (item.children?.length) {
			this.toggleSubmenu(item);
			return;
		}
		// Navigate: close mobile sidebar
		if (this.viewportService.isMobileView()) {
			this.closeMobileSidebar();
		}
	}

	isSubmenuExpanded(item: SidebarMenuItem): boolean {
		return this.expandedSubmenuId === item.id;
	}

	// --- Profile ---

	toggleProfileMenu(): void {
		this.isProfileMenuOpen = !this.isProfileMenuOpen;
	}

	openProfileModal(): void {
		this.menuService.setProfileModalState(true);
		if (this.viewportService.isMobileView()) {
			this.isProfileMenuOpen = false;
			this.closeMobileSidebar();
		}
	}

	handleLogout(): void {
		this.isProfileMenuOpen = false;
		this.authService.logout().subscribe({
			next: () => {
				if (this.viewportService.isMobileView()) {
					this.closeMobileSidebar();
				}
			},
		});
	}

	getUserAvatar(): string {
		return this.currentUser?.avatarUrl || 'assets/layout/images/sticker-edu.png';
	}

	// --- Init helpers ---

	private initUser(): void {
		this.currentUser = this.currentUserService.getCurrentUser();
		this.userName = this.currentUser?.displayName || 'Usuario';
		this.userRole = this.currentUser?.role?.name || '';
		this.currentUserId = this.currentUserService.getUserId();
		this.isClient = this.currentUserService.hasRole('client');
	}

	private buildMenu(): void {
		const allItems: SidebarMenuItem[] = [
			{
				id: 'dashboard',
				labelKey: 'layout.sidebar.menu.dashboard',
				icon: 'LayoutDashboard',
				routerLink: ['/dashboard'],
				permission: 'page:view-dashboard',
			},
			{
				id: 'boost',
				labelKey: 'layout.sidebar.menu.boost',
				icon: 'Rocket',
				routerLink: ['/boost'],
			},
			{
				id: 'subscriptions',
				labelKey: 'layout.sidebar.menu.subscriptions',
				icon: 'RefreshCw',
				routerLink: ['/subscriptions'],
				featureFlag: 'subscriptions',
			},
			{
				id: 'orders',
				labelKey: 'layout.sidebar.menu.orders',
				icon: 'ShoppingCart',
				routerLink: ['/orders'],
			},
			{
				id: 'my-subscriptions',
				labelKey: 'layout.sidebar.menu.mySubscriptions',
				icon: 'ListChecks',
				routerLink: ['/my-subscriptions'],
				featureFlag: 'subscriptions',
			},
			{
				id: 'requests',
				labelKey: 'layout.sidebar.menu.requests',
				icon: 'ClipboardCheck',
				routerLink: ['/requests'],
				permission: 'page:view-requests',
			},
			{
				id: 'users',
				labelKey: 'layout.sidebar.menu.users',
				icon: 'Users',
				routerLink: ['/users'],
				permission: 'page:view-users',
			},
			{
				id: 'configuration',
				labelKey: 'layout.sidebar.menu.configuration',
				icon: 'Settings',
				routerLink: ['/configuration'],
				permission: 'page:view-configuration',
				section: 'config',
			},
		];

		this.menuItems = this.filterByPermission(allItems);
	}

	private filterByPermission(items: SidebarMenuItem[]): SidebarMenuItem[] {
		return items
			.filter((item) => this.hasPermission(item))
			.map((item) => {
				if (item.children?.length) {
					return { ...item, children: this.filterByPermission(item.children) };
				}
				return item;
			});
	}

	private hasPermission(item: SidebarMenuItem): boolean {
		if (item.featureFlag) {
			if (!this.brandingService.current?.featureFlags?.[item.featureFlag]) return false;
		}
		if (!item.permission) return true;
		return this.currentUserService.hasPermission(item.permission);
	}
}
